import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useAuth } from '../../App';
import { Seo } from '../../components/seo/Seo';
import { PublicLayout } from '../../components/public/PublicLayout';
import { Container, HeroSection, StepsSection, FeatureGrid, TrustSection, FaqAccordion, CtaBand } from '../../components/public/PublicSections';
import { SectionHeading } from '../../components/public/SectionHeading';
import { ShowcaseSection } from '../../components/public/ShowcaseSection';
import { HOME, FEATURES, FAQS } from '../../seo/publicContent';
import { pageMeta, pagePath, alternatesFor } from '../../seo/seoConfig.mjs';
import { organizationSchema, websiteSchema, softwareSchema } from '../../seo/structuredData';

export default function PublicHomePage({ lang }) {
  const { user } = useAuth();
  const copy = HOME[lang];
  const meta = pageMeta('home', lang);
  const appHref = user ? (user.onboarding_complete ? '/dashboard' : '/onboarding') : '/auth';
  const faqTeaser = FAQS[lang].slice(0, 3);

  return (
    <PublicLayout lang={lang} pageKey="home">
      <Seo
        title={meta.title}
        description={meta.description}
        keywords={meta.keywords}
        path={pagePath('home', lang)}
        lang={lang}
        alternates={alternatesFor('home')}
        jsonLd={[organizationSchema(lang), websiteSchema(lang), softwareSchema(lang)]}
      />

      <HeroSection copy={copy} appHref={appHref} featuresHref={pagePath('features', lang)} />
      <StepsSection copy={copy} />

      <section className="py-16 sm:py-24 border-t border-white/[0.06]" aria-labelledby="features-title">
        <Container>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <SectionHeading title={copy.featuresTitle} sub={copy.featuresSub} testId="features-heading" />
            <Link to={pagePath('features', lang)} className="inline-flex items-center gap-2 text-sm font-medium text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200 shrink-0" data-testid="home-features-link">
              {copy.featuresLink} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="mt-12">
            <FeatureGrid features={FEATURES} lang={lang} />
          </div>
        </Container>
      </section>

      <ShowcaseSection lang={lang} appHref={appHref} />
      <TrustSection copy={copy} />

      <section className="py-16 sm:py-24 border-t border-white/[0.06]" aria-labelledby="faq-title">
        <Container className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <SectionHeading title={copy.faqTitle} testId="home-faq-heading" />
            <Link to={pagePath('faq', lang)} className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-[var(--lux-gold)] hover:text-[var(--lux-amber)] transition-colors duration-200" data-testid="home-faq-link">
              {copy.faqLink} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="lg:col-span-8">
            <FaqAccordion faqs={faqTeaser} testId="home-faq-accordion" />
          </div>
        </Container>
      </section>

      <CtaBand title={copy.finalTitle} sub={copy.finalSub} ctaLabel={copy.ctaPrimary} to={appHref} testId="home-final-cta" />
    </PublicLayout>
  );
}
